import { extractApiErrorMessage } from '@cartwheel/api'
import {
  type CommercialCostEstimateInput,
  type CostEstimate,
  clientCostEstimateShowQueryKey,
  useClientCostEstimateCreateMutation,
  useClientCostEstimateUpdateMutation,
} from '@cartwheel/api/v1'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Button } from '../button'
import { TextInput } from '../text-input'
import { CurrencyInput } from './currency-input'

interface CommercialEstimateFormProps {
  clientId: string
  estimate: CostEstimate | null
  onSaved: () => void
  onCancel: () => void
}

// Labels wrap their input so the browser auto-associates via DOM containment —
// no useId/htmlFor needed, and Playwright's get_by_label resolves cleanly.
function CurrencyField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string | null
  onChange: (value: string | null) => void
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: CurrencyInput renders the <input>
    <label className="flex justify-between items-center py-2 px-4 border-b border-gray-stroke">
      <span className="font-medium">{label}</span>
      <CurrencyInput value={value} onChange={onChange} />
    </label>
  )
}

export function CommercialEstimateForm({
  clientId,
  estimate,
  onSaved,
  onCancel,
}: CommercialEstimateFormProps) {
  const editable = estimate?.editable
  const [individualDeductible, setIndividualDeductible] = useState<string | null>(
    editable?.individual_deductible ?? null
  )
  const [individualRemaining, setIndividualRemaining] = useState<string | null>(
    editable?.amount_remaining_individual_deductible ?? null
  )
  const [familyDeductible, setFamilyDeductible] = useState<string | null>(
    editable?.family_deductible ?? null
  )
  const [familyRemaining, setFamilyRemaining] = useState<string | null>(
    editable?.amount_remaining_family_deductible ?? null
  )
  const [copay, setCopay] = useState<string | null>(editable?.copay ?? null)
  const [coinsurance, setCoinsurance] = useState<number | null>(
    editable?.coinsurance_estimate ?? null
  )
  const [sessionLimit, setSessionLimit] = useState<number | null>(editable?.session_limit ?? null)
  const [customMessage, setCustomMessage] = useState<string>(editable?.custom_message ?? '')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: clientCostEstimateShowQueryKey({ path: { client_id: clientId } }),
    })

  const createMutation = useClientCostEstimateCreateMutation({
    onSuccess: () => {
      invalidate()
      onSaved()
    },
    onError: (error) =>
      setErrorMessage(extractApiErrorMessage(error, 'Something went wrong. Please try again.')),
  })
  const updateMutation = useClientCostEstimateUpdateMutation({
    onSuccess: () => {
      invalidate()
      onSaved()
    },
    onError: (error) =>
      setErrorMessage(extractApiErrorMessage(error, 'Something went wrong. Please try again.')),
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setErrorMessage(null)

    const body: CommercialCostEstimateInput = {
      estimate_type: 'commercial',
      individual_deductible: individualDeductible,
      amount_remaining_individual_deductible: individualRemaining,
      family_deductible: familyDeductible,
      amount_remaining_family_deductible: familyRemaining,
      copay,
      coinsurance_estimate: coinsurance,
      session_limit: sessionLimit,
      custom_message: customMessage.trim() === '' ? null : customMessage,
    }

    if (estimate?.id) {
      updateMutation.mutate({ path: { client_id: clientId, id: estimate.id }, body })
    } else {
      createMutation.mutate({ path: { client_id: clientId }, body })
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <CurrencyField
        label="Individual Deductible"
        value={individualDeductible}
        onChange={setIndividualDeductible}
      />
      <CurrencyField
        label="Individual Deductible Remaining"
        value={individualRemaining}
        onChange={setIndividualRemaining}
      />
      <CurrencyField
        label="Family Deductible"
        value={familyDeductible}
        onChange={setFamilyDeductible}
      />
      <CurrencyField
        label="Family Deductible Remaining"
        value={familyRemaining}
        onChange={setFamilyRemaining}
      />
      <CurrencyField label="Copay" value={copay} onChange={setCopay} />

      {/* biome-ignore lint/a11y/noLabelWithoutControl: TextInput renders the <input> */}
      <label className="flex justify-between items-center py-2 px-4 border-b border-gray-stroke">
        <span className="font-medium">Coinsurance</span>
        <div className="flex items-center">
          <span className="mr-1">%</span>
          <span className="w-[125px]">
            <TextInput
              type="number"
              inputMode="numeric"
              min={0}
              max={100}
              step={1}
              value={coinsurance ?? ''}
              onChange={(event) => {
                const next = event.target.value
                setCoinsurance(next === '' ? null : Number(next))
              }}
              className="text-right h-8"
            />
          </span>
        </div>
      </label>

      {/* biome-ignore lint/a11y/noLabelWithoutControl: TextInput renders the <input> */}
      <label className="flex justify-between items-center py-2 px-4 border-b border-gray-stroke">
        <span className="font-medium">Session Limits (optional)</span>
        <span className="w-[125px] block">
          <TextInput
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={sessionLimit ?? ''}
            onChange={(event) => {
              const next = event.target.value
              setSessionLimit(next === '' ? null : Number(next))
            }}
            className="text-right h-8"
          />
        </span>
      </label>

      {/* biome-ignore lint/a11y/noLabelWithoutControl: TextInput renders the <input> */}
      <label className="my-6 grid gap-4">
        <span className="flex justify-between">
          <span className="font-ginto-nord-md text-charcoal text-lg font-semibold">
            Custom message to family
          </span>
          <span className="text-charcoal-50">(optional)</span>
        </span>
        <TextInput
          value={customMessage}
          onChange={(event) => setCustomMessage(event.target.value)}
          placeholder="example: This cost estimate applies as long as your insurance remains active."
        />
      </label>

      {errorMessage && <div className="mt-2 text-red-700 text-sm text-right">{errorMessage}</div>}

      <div className="flex justify-end gap-2 mt-10">
        <Button type="button" variant="tertiary" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isPending}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  )
}
